// Real content for abhinavjoshi.com homepage.
// Image URLs point to files already hosted on the live WordPress site's media library.
// If any filename changes on the live site, update the corresponding constant below.

export const SITE = {
  logo: "https://abhinavjoshi.com/wp-content/uploads/2024/08/aj.png",
  name: "AbhinavJoshi",
  email: "mail@abhinavjoshi.com",
  linkedin: "https://www.linkedin.com/in/imabhinavjoshi/",
  calendly: "https://calendly.com/growthabhinav/30min",
  // Facebook/Instagram intentionally omitted. The live site currently links these to the
  // homepage itself rather than real profiles. Add real URLs here once available, don't
  // reuse placeholder/broken links.
};

export const logos = [
  { src: "https://abhinavjoshi.com/wp-content/uploads/2024/08/skedda-g.png", alt: "Skedda" },
  { src: "https://abhinavjoshi.com/wp-content/uploads/2026/01/ss_logo.png", alt: "Spike ROAS" },
  { src: "https://abhinavjoshi.com/wp-content/uploads/2024/08/sr-g.png", alt: "SR Global" },
  { src: "https://abhinavjoshi.com/wp-content/uploads/2026/01/logo-fortune-1.png", alt: "Fortune" },
  { src: "https://abhinavjoshi.com/wp-content/uploads/2026/01/matter-logo.png", alt: "Matter" },
  { src: "https://abhinavjoshi.com/wp-content/uploads/2026/01/bits-pillani-2-1.png", alt: "BITS Pilani" },
  { src: "/assets/madras-sarees-logo.png", alt: "Madras Sarees" },
  { src: "/assets/monday-logo.png", alt: "monday.com" },
];

// "Tools" section: the three real tools built for abhinavjoshi.com, each linking to the
// actual live tool rather than a generic "Learn more" placeholder.
export const tools = [
  {
    title: "Discovery & strategy engine",
    headline: "From Blank Page to Full Campaign Strategy in Minutes",
    desc: "Answer a few questions about your business and get a full campaign strategy, budget plan, and ad copy, generated instantly.",
    href: "https://abhinavjoshi.com/campaigns/",
    cta: "Try it free",
    usps: ["Instant strategy generation", "Platform-specific budgets", "Ready-to-use ad copy", "Owner & expert modes"],
    image: { src: "/assets/tool-campaigns.png", alt: "Discovery and strategy engine turning a document into a full campaign dashboard" },
  },
  {
    title: "Google Ads campaign optimizer",
    headline: "Find Wasted Ad Spend Before It Costs You More",
    desc: "Upload your Google Ads export and get an instant read on wasted spend, impression share gaps, and optimization priorities.",
    href: "https://abhinavjoshi.com/campaign_optimizer/",
    cta: "Open the tool",
    usps: ["Wasted spend detection", "Impression share gaps", "Prioritized fixes", "Works with your existing export"],
    image: { src: "/assets/tool-campaign-optimizer.png", alt: "Campaign optimizer dashboard showing Q4 performance and a 247 percent lift in conversions" },
  },
  {
    title: "SEO audit tool",
    headline: "A 22-Point Technical SEO Check, Graded Instantly",
    desc: "Run a 22-point technical SEO check on any URL: page speed, meta tags, structured data, and more, graded instantly.",
    href: "https://abhinavjoshi.com/tools/seo-audit.html",
    cta: "Run an audit",
    usps: ["Page speed analysis", "Meta tag audit", "Structured data check", "Instant grading"],
    image: { src: "/assets/tool-seo-audit.png", alt: "SEO audit dashboard showing a 98.7 percent performance score with load time and speed metrics" },
  },
];

// Verified real stats only; no invented percentages.
// Compact list for the Navbar "Tools" dropdown. Reuses the same URLs as the `tools` array
// above so there's a single source of truth if a tool's link ever changes.
export const navTools = [
  { label: "Campaigns", href: tools[0].href },
  { label: "Campaign Optimizer", href: tools[1].href },
  { label: "SEO Tool", href: tools[2].href },
];

export const trustStats = [
  { value: "7+", label: "Years experience" },
  { value: "20+", label: "Projects delivered" },
  { value: "$2M+", label: "Ad spend managed" },
  { value: "4.5+", label: "Average ROAS" },
];

export const successStory = {
  image: "https://abhinavjoshi.com/wp-content/uploads/2024/08/shopify-march-nov-24.png",
  tag: "A success story",
  title: "Results that speak louder than promises",
  desc: "For Madras Sarees, a D2C saree brand, ₹4+ crore in revenue generated across Mar 2024–Jan 2025. This wasn't about running ads. It was about building a predictable revenue system aligned with business outcomes.",
  href: "/success-stories/madras-sarees-revenue-growth",
  ctaLabel: "Read the full success story",
};

// List of case studies for the /success-stories index page and the /success-stories/:slug
// detail pages. Each entry follows the rich schema below; add one object per project once
// real, verified numbers are available. Do not add a story here with placeholder/fabricated
// figures; leave it out of this array until the real data is confirmed.
export const successStories = [
  {
    slug: "madras-sarees-revenue-growth",

    // ISO date this page went live. Used for article:published_time in the
    // social-share preview (og.php) so LinkedIn/Facebook show a real date
    // instead of leaving it blank. Author is always "Abhinav Joshi" and is
    // set globally in og.php, not per-story.
    publishedDate: "2026-09-02",

    // ---- Hero banner ----
    // Currently reusing the same product photo used for the thumbnail. Swap in a
    // dedicated wide/landscape shot here later if you want the hero to differ
    // from the card thumbnail.
    bannerImage: "/assets/success-stories/madras-sarees-thumb.jpg",

    // ---- Listing/card preview ----
    clientName: "Madras Sarees",
    industry: "D2C / Fashion (Sarees)",
    tag: "D2C / Apparel",
    listingTitle: "₹4+ crore in revenue and a 7.69x blended ROAS for a D2C saree brand",
    listingSummary:
      "How restructured campaigns, a retention layer, and language-specific creative turned a plateaued sub-3x ROAS account into a predictable, multi-crore revenue engine.",
    thumbnailImage: "/assets/success-stories/madras-sarees-thumb.jpg",

    // ---- 1. Executive summary ----
    executiveSummary:
      "Madras Sarees came in stuck below 3x ROAS with a ₹1,200 CAC and no vertical growth. Over the engagement, revenue scaled from ₹18L to ₹58L a month, blended ROAS reached 7.69x across 220 campaigns, CAC dropped to ₹800, and returns fell 73%, driven by a rebuilt campaign structure, systematic A/B testing, and a retention layer that recovered ₹5L+ on its own.",

    // ---- Four hero highlight cards (short one-liners, distinct from the full sections below) ----
    highlights: {
      challenge: "Sub-3x ROAS, high CAC, no growth.",
      approach: "Rebuilt campaigns, tested relentlessly, added retention.",
      timeline: "Mar 2024 to Jan 2025, month over month.",
      result: "₹4.07 Cr revenue at 7.69x ROAS.",
    },

    // ---- SEO ----
    seo: {
      metaTitle: "Madras Sarees Success Story: Rs 4+ Cr Revenue, 7.69x ROAS",
      metaDescription:
        "How restructured campaigns, A/B testing, and a retention layer took Madras Sarees from sub-3x ROAS to Rs 4.07 crore in revenue at a 7.69x blended ROAS.",
    },

    // ---- 2. Client overview ----
    clientOverview: {
      about:
        "Madras Sarees is a D2C saree brand selling everyday and occasion-wear sarees (cotton, linen, silk cotton, semi silk, and more) direct to consumers online, targeting modern Indian women across work, casual, and festive occasions.",
      industry: "D2C / Fashion (Sarees)",
      scale: "Direct-to-consumer online boutique, Shopify storefront, primarily South Indian market with growing pan-India reach",
      context:
        "The account had plateaued: ROAS hadn't crossed 3x, CAC sat around ₹1,200, and there was no vertical growth or creative refresh to unstick performance.",
    },

    // ---- 3. Challenge ----
    challenge: {
      summary:
        "Performance and revenue had stalled. ROAS stayed stable but never broke past 3x, CAC was high at ₹1,200, and stale creative meant there was no lever left to pull for growth.",
      points: [
        "ROAS plateaued below 3x with no vertical growth month over month",
        "CAC stuck around ₹1,200",
        "No creative refresh cycle in place",
      ],
    },

    // ---- 4. Solution ----
    solution: {
      summary:
        "Rebuilt the campaign structure and testing approach from the ground up, layered in a dedicated retention program, and used Shopify-side plugins to lift average order value alongside the media changes.",
      strategies: [
        "Restructured campaigns, targeting approach, and ad communication/messaging pointers",
        "Ran systematic A/B testing across creatives, offers, communication angles, and audiences",
        "Integrated smart Shopify plugins to increase add-to-cart rate, lifting average order value",
        "Built out WhatsApp-based abandoned cart and retention campaigns to recover lost orders",
        "Used language-specific creative and communication to unlock the North Indian market after initial resistance there",
      ],
      platforms: ["Meta Ads", "Google Ads", "WhatsApp Campaigns", "Shopify"],
    },

    // ---- 5. Implementation ----
    implementation: {
      timeline: "Mar 2024 – Jan 2025, scaled progressively month over month",
      milestones: [
        { phase: "April", detail: "₹18L monthly revenue" },
        { phase: "May", detail: "₹22L monthly revenue" },
        { phase: "July", detail: "₹38L monthly revenue" },
        { phase: "October", detail: "₹58L monthly revenue" },
      ],
      methodology:
        "Continuous A/B testing across creatives, offers, and audiences, with a parallel retention/WhatsApp workstream running alongside acquisition campaigns on Meta and Google.",
    },

    // ---- 6. Results ----
    results: {
      summary:
        "Revenue scaled steadily month over month while ROAS moved from a sub-3x plateau to a 7.69x blended figure across 220 live campaigns, CAC dropped by a third, and returns fell sharply, all while a dedicated retention program recovered orders that would otherwise have been lost.",
      metrics: [
        { label: "Total revenue (Mar 2024–Jan 2025)", value: "₹4.07 Cr", trend: "up-good" },
        { label: "Blended ROAS", value: "7.69x", trend: "up-good" },
        { label: "CAC", value: "₹800 (from ₹1,200)", trend: "down-good" },
        { label: "Returns", value: "-73%", trend: "down-good" },
        { label: "Orders recovered via retention", value: "349 (₹5.05L)", trend: "up-good" },
      ],
    },

    // ---- 7. Evidence ----
    evidenceChart: {
      type: "line",
      labels: ["April", "May", "July", "October"],
      datasetLabel: "Monthly revenue (₹ lakhs)",
      data: [18, 22, 38, 58],
      valuePrefix: "₹",
      valueSuffix: "L",
    },
    evidenceNote:
      "Revenue figures from Shopify reporting; total sales across Mar 2024–Jan 2025 came to ₹4,07,55,515.65. Blended ROAS of 7.69x is measured across 220 active campaigns in Ads Manager. Retention numbers (349 orders recovered, ₹5,05,843 recovered, 2,782 messages sent, 14.63% link-click rate) are from the WhatsApp abandoned cart recovery program specifically, not a store-wide repeat purchase rate.",

    // ---- 8. Lessons learned ----
    lessonsLearned: [
      {
        challenge: "Initial attempts to break into the North Indian market weren't converting.",
        resolution:
          "Switched to language-specific creative and messaging for that region, which unlocked profitable performance in North Indian markets.",
      },
    ],

    // ---- 9. Testimonial ----
    testimonial: null,

    // ---- 10. ROI ----
    roi: {
      summary:
        "₹3.43 Cr of the total revenue came from performance marketing and ₹57L from retention marketing, meaning the retention layer alone contributed meaningfully on top of acquisition spend, while CAC fell by roughly a third.",
      paybackPeriod: null,
    },

    // ---- 11. Next steps ----
    nextSteps: [
      "Launch the brand in the USA market with fresh inventory and a new market-entry approach",
    ],

    // ---- 12. Appendix (optional) ----
    appendix: {
      title: "Methodology & data notes",
      content:
        "Revenue and order figures are sourced directly from Shopify reporting and Meta Ads Manager exports. Retention metrics (orders recovered, amount recovered, link-click rate) are specific to the WhatsApp abandoned cart program and should not be read as an overall repeat purchase rate.",
    },

    // ---- Bottom CTA ----
    cta: {
      headline: "Want a revenue engine like this for your D2C brand?",
      body: "This is the exact playbook I ran for Madras Sarees: restructured campaigns, real A/B testing discipline, and a retention layer that pays for itself.",
    },

    href: "/connect",
  },

  {
    slug: "softwaresuggest-global-ppc-growth",

    // ISO date this page went live, see note on the Madras Sarees entry above.
    publishedDate: "2026-09-02",

    // ---- Hero banner ----
    bannerImage: "/assets/success-stories/softwaresuggest-thumb.jpg",

    // ---- Listing/card preview ----
    clientName: "SoftwareSuggest",
    industry: "B2B SaaS / Software Discovery & Review Platform",
    tag: "B2B / SaaS",
    listingTitle: "$700K+ in annual PPC profit scaling a B2B SaaS marketplace across 6 global regions",
    listingSummary:
      "How fixing broken conversion tracking and rebuilding campaigns region by region turned a sub-3x ROAS, India-only PPC account into a profitable global growth engine.",
    thumbnailImage: "/assets/success-stories/softwaresuggest-thumb.jpg",

    // ---- 1. Executive summary ----
    executiveSummary:
      "SoftwareSuggest came in running India-only PPC campaigns on a limited budget, averaging a 2-3x ROAS with high CPL and no profit in global markets. After fixing conversion tracking end-to-end and rebuilding campaigns and creative, PPC scaled from a single-market, barely-profitable channel in 2021 to a global growth engine generating $700K+ in annual profit by 2024 across India, GCC, US-Canada, Australia-NZ, Southeast Asia, and parts of Europe.",

    // ---- Four hero highlight cards (short one-liners, distinct from the full sections below) ----
    highlights: {
      challenge: "Sub-3x ROAS, high CPL, no profit outside India.",
      approach: "Fixed tracking, then scaled region by region.",
      timeline: "2021 to 2024, expanding to 6+ regions.",
      result: "$700K+ annual PPC profit by 2024.",
    },

    // ---- SEO ----
    seo: {
      metaTitle: "SoftwareSuggest Success Story: $700K+ Annual PPC Profit",
      metaDescription:
        "How fixing conversion tracking and scaling region by region turned SoftwareSuggest's India-only PPC account into a profitable global growth engine generating $700K+ a year.",
    },

    // ---- 2. Client overview ----
    clientOverview: {
      about:
        "SoftwareSuggest is a B2B software discovery and recommendation platform that helps businesses find the right software for their needs across hundreds of categories, while helping software vendors connect with verified buyers globally.",
      industry: "B2B SaaS / Software Discovery & Review Platform",
      scale: "Founded 2014, headquartered in Ahmedabad, India; a global B2B software marketplace",
      context:
        "In 2021, paid campaigns ran only in India on a limited budget, averaging a 2-3x ROAS with high CPL, slow delivery, and no profitable presence in global markets.",
    },

    // ---- 3. Challenge ----
    challenge: {
      summary:
        "The account was confined to India with a limited PPC budget and a stable-but-low 2-3x ROAS, and every attempt to expand into global markets failed to turn a profit.",
      points: [
        "Campaigns ran only in India on a limited PPC budget, averaging 2-3x ROAS",
        "Limited client delivery with slow delivery pacing, minimal categories, and minimum locations covered",
        "No profitable presence in global markets",
        "High CPL and high turnaround time",
        "Missing detailed tracking, with conversion discrepancies between the CRM and the ad dashboards",
      ],
    },

    // ---- 4. Solution ----
    solution: {
      summary:
        "Reverse-engineered the existing account to find the real gaps, then rebuilt tracking accuracy first, followed by landing page and creative work, before scaling spend into new markets.",
      strategies: [
        "Fixed conversion tracking with Google Tag Manager, server-side tracking, GA4, and Meta custom conversions for accurate reporting",
        "Used session recordings to identify and fix landing page CRO issues for better engagement",
        "Reworked landing page communication, ad copy, offerings, and CTAs for clarity",
      ],
      platforms: ["Google Ads", "Meta Ads", "Bing Ads", "Quora Ads", "LinkedIn Ads"],
    },

    // ---- 5. Implementation ----
    implementation: {
      timeline: "2021 to 2024, expanded progressively across regions and categories",
      milestones: [
        {
          phase: "2021",
          detail: "Fixed campaign fundamentals, scaled PPC spend 3x, ROI up 8% vs 2020, and brought CPL under control",
        },
        {
          phase: "2022",
          detail: "6x growth in the Indian market, turned profitable in the GCC region, CPL down 20%, reduced delivery time",
        },
        {
          phase: "2023",
          detail:
            "Crossed $105K+ profit in Q1 alone and continued every quarter after; expanded delivery into US-Canada, Australia-NZ, GCC, Southeast Asia, and parts of Europe",
        },
        {
          phase: "2024",
          detail:
            "Global delivery up 15%, MER up 8%, higher global client retention, and $700K+ in annual profit from PPC alone",
        },
      ],
      methodology:
        "Reverse-engineered the existing account to find the real gaps, rebuilt tracking and creative first, then scaled spend region by region as each market proved profitable.",
    },

    // ---- 6. Results ----
    results: {
      summary:
        "PPC went from a single-market, barely-profitable channel to a global growth engine, crossing $105K+ in quarterly profit by early 2023 and $700K+ in annual profit by 2024, while CPL fell and delivery expanded into six additional regions.",
      metrics: [
        { label: "Annual PPC profit (2024)", value: "$700K+", trend: "up-good" },
        { label: "Q1 2023 profit", value: "$105K+", trend: "up-good" },
        { label: "Indian market growth (2022)", value: "6x", trend: "up-good" },
        { label: "CPL change (2022)", value: "-20%", trend: "down-good" },
        { label: "Global delivery growth (2024)", value: "+15%", trend: "up-good" },
      ],
    },

    // ---- 7. Evidence ----
    // No evidenceChart for this story: the figures shared span different units across
    // years (spend multiples, market-growth multiples, quarterly profit, annual profit),
    // so a single chart would misrepresent the trend rather than clarify it.

    // ---- 8. Lessons learned ----
    // None provided for this engagement yet.
    lessonsLearned: [],

    // ---- 9. Testimonial ----
    testimonial: null,

    // ---- 10. ROI ----
    roi: {
      summary:
        "PPC profit crossed $105K+ in Q1 2023 alone and continued every quarter after, reaching $700K+ in annual profit by 2024, a shift from an unprofitable global account in 2021.",
      paybackPeriod: null,
    },

    // ---- 11. Next steps ----
    // None provided for this engagement yet.
    nextSteps: [],

    // ---- 12. Appendix (optional) ----
    appendix: {
      title: "Methodology & data notes",
      content:
        "Figures reflect PPC performance data shared directly by the SoftwareSuggest team across 2021-2024 (spend scale, ROAS/CPL movement, market growth, and profit figures). Unlike the Madras Sarees case study, these numbers were not independently cross-referenced against dashboard or CRM screenshots.",
    },

    // ---- Bottom CTA ----
    cta: {
      headline: "Want PPC that turns a global SaaS marketplace profitable?",
      body: "This is the same playbook I ran for SoftwareSuggest: fixed tracking, sharper creative, and a region-by-region scale-up that turned a sub-3x ROAS account into $700K+ in annual profit.",
    },

    href: "/connect",
  },

  {
    slug: "5th-dimension-realty-lead-generation-growth",

    // ISO date this page went live, see note on the Madras Sarees entry above.
    publishedDate: "2026-09-02",

    // ---- Hero banner ----
    bannerImage: "/assets/success-stories/5th-dimension-realty-thumb.jpg",

    // ---- Listing/card preview ----
    clientName: "5th Dimension Realty",
    industry: "B2B Commercial Real Estate",
    tag: "B2B / Real Estate",
    listingTitle: "377 leads and a 6.2x average ROAS for a brand-new B2B real estate brokerage",
    listingSummary:
      "How building a complete lead-gen engine from scratch, tracking, creative, and automation, took a company with zero campaign history to a 6.2x average monthly ROAS within its first live quarter.",
    thumbnailImage: "/assets/success-stories/5th-dimension-realty-thumb.jpg",

    // ---- 1. Executive summary ----
    executiveSummary:
      "5th Dimension Realty launched in October 2024 with no PPC history to build from and a limited budget. Starting from a clean slate, campaigns went live within a month, and by the second live month the account was already averaging a 6.2x ROAS, generating 377 leads and outranking established competitors like WeWork, Smartworks, and Awfis in auction from month two onward.",

    // ---- Four hero highlight cards (short one-liners, distinct from the full sections below) ----
    highlights: {
      challenge: "Brand-new account, no history, limited budget.",
      approach: "Built tracking, creative, and lead nurturing from scratch.",
      timeline: "Oct 2024 to Feb 2025, live within a month.",
      result: "6.2x average monthly ROAS from month two.",
    },

    // ---- SEO ----
    seo: {
      metaTitle: "5th Dimension Realty Success Story: 6.2x Avg Monthly ROAS",
      metaDescription:
        "How 5th Dimension Realty went from zero PPC history to a 6.2x average monthly ROAS and 377 leads in its first live quarter, built from scratch with Google Ads, Meta Ads, and lead automation.",
    },

    // ---- 2. Client overview ----
    clientOverview: {
      about:
        "5th Dimension Realty is a Pune-based commercial real estate brokerage that helps businesses, from SMEs to enterprises, find co-working and managed office spaces across major Indian cities.",
      industry: "B2B Commercial Real Estate",
      scale:
        "Founded October 2024, headquartered in Pune; serves Pune, Mumbai, Delhi, Bangalore, Gurgaon, and Hyderabad",
      context:
        "The company had just launched with no PPC campaign history to learn from and a limited budget, and needed to generate real business leads from day one.",
    },

    // ---- 3. Challenge ----
    challenge: {
      summary:
        "As a brand-new company, there was no existing lead-generation data to build on, and the account needed to start producing qualified leads quickly on a limited budget.",
      points: [
        "No past PPC data or learnings to build on, since the company had just launched",
        "Limited marketing budget to work with from the start",
        "Needed to generate real business leads, not just traffic or clicks",
      ],
    },

    // ---- 4. Solution ----
    solution: {
      summary:
        "Built the entire marketing engine from scratch, campaign structure, creative, landing pages, tracking, and lead nurturing, rather than optimizing an existing setup.",
      strategies: [
        "Structured a complete marketing strategy from scratch across campaigns, communication, and creative",
        "Designed landing pages with strong CRO built in from the start",
        "Set up complete event tracking, conversion tracking, and call tracking (CTM) integration",
        "Used session recordings to understand user behavior and improve engagement",
        "Built lead automation to nurture leads at every stage of the funnel",
      ],
      platforms: ["Google Ads", "Meta Ads", "Gupshup"],
    },

    // ---- 5. Implementation ----
    implementation: {
      timeline: "Oct 2024 to Feb 2025, live within the first month",
      milestones: [
        {
          phase: "Oct 2024",
          detail: "Campaign setup, event tracking, conversion tracking, and research completed; account ready to go live",
        },
        {
          phase: "Nov 2024",
          detail:
            "Campaigns went live on Google and Meta; 50 leads at a ₹600 CPL, average CPL ₹300 to ₹500, turnaround time 1 to 3 months, no business conversions yet",
        },
        {
          phase: "Dec 2024",
          detail: "100 leads at a ₹400 CPL, 3 business conversions, ₹2.4L revenue, 6x ROAS",
        },
        {
          phase: "Jan 2025",
          detail: "105 leads at a ₹380 CPL, 4 business conversions, ₹3.2L revenue, 8x ROAS, the strongest month yet",
        },
        {
          phase: "Feb 2025",
          detail: "122 leads at a ₹410 CPL, 3 business conversions, ₹2.4L revenue, 5x ROAS",
        },
      ],
      methodology:
        "Built and launched everything within the first month, then optimized month over month using real conversion data as it came in, since there was no prior account history to start from.",
    },

    // ---- 6. Results ----
    results: {
      summary:
        "Lead volume grew every month while CPL fell, and the account averaged a 6.2x ROAS per month from December 2024 onward, the point at which business conversions started coming through. By its second live month, the account was already winning auction share against established competitors like WeWork, Smartworks, and Awfis, despite having zero prior campaign history.",
      metrics: [
        { label: "Avg monthly ROAS", value: "6.2x", trend: "up-good" },
        { label: "Total leads (Nov 2024 to Feb 2025)", value: "377", trend: "up-good" },
        { label: "Revenue (Dec 2024 to Feb 2025)", value: "₹8L", trend: "up-good" },
        { label: "CPL change (Nov to Feb)", value: "-32%", trend: "down-good" },
      ],
    },

    // ---- 7. Evidence ----
    evidenceChart: {
      type: "line",
      labels: ["Nov", "Dec", "Jan", "Feb"],
      datasetLabel: "Monthly leads generated",
      data: [50, 100, 105, 122],
    },
    evidenceNote:
      "Lead volume grew from 50 in the first live month (Nov 2024) to 122 by Feb 2025, drawn from monthly Google Ads and Meta Ads reporting. CPL fell from ₹600 to as low as ₹380 over the same period, and the account averaged a 6.2x ROAS per month from December 2024 onward, once business conversions began being tracked.",

    // ---- 8. Lessons learned ----
    // None provided for this engagement yet.
    lessonsLearned: [],

    // ---- 9. Testimonial ----
    testimonial: null,

    // ---- 10. ROI ----
    roi: {
      summary:
        "Monthly revenue attributable to the campaign reached ₹8 lakh across three converting months (Dec 2024 to Feb 2025), at an average 6.2x ROAS, on a monthly spend of ₹30,000 to ₹50,000. The account also won auction share against established competitors like WeWork, Smartworks, and Awfis from its second live month, despite having no prior campaign history to build from.",
      paybackPeriod: "Positive ROAS by the second live month (Dec 2024), the first month with tracked business conversions.",
    },

    // ---- 11. Next steps ----
    // None provided for this engagement yet.
    nextSteps: [],

    // ---- 12. Appendix (optional) ----
    appendix: {
      title: "Methodology & data notes",
      content:
        "Figures reflect monthly performance data (spend, CPL, leads, site visits, business conversions, revenue, and ROAS) shared directly by the 5th Dimension Realty team for Nov 2024 through Feb 2025. As with the SoftwareSuggest case study, these numbers were not independently cross-referenced against dashboard or CRM screenshots.",
    },

    // ---- Bottom CTA ----
    cta: {
      headline: "Starting a new brand with zero campaign history?",
      body: "This is the same playbook I ran for 5th Dimension Realty: build tracking and creative right the first time, then let real data drive optimization from month one.",
    },

    href: "/connect",
  },

  {
    slug: "ismt-stock-market-training-lead-generation-growth",

    // ISO date this page went live, see note on the Madras Sarees entry above.
    publishedDate: "2026-09-16",

    // ---- Hero banner ----
    bannerImage: "/assets/success-stories/ismt-thumb.jpg",

    // ---- Listing/card preview ----
    clientName: "ISMT (Institute of Stock Market Training)",
    industry: "B2C EdTech / Stock Market & Trading Education",
    tag: "B2C / EdTech",
    listingTitle: "CPL down 72% and a 6.4x average ROAS for a stock market training institute",
    listingSummary:
      "How fixing broken tracking, adding CRM, and continuous creative testing turned ISMT's lead-gen account around, from a ₹480 CPL with no conversion visibility to a ₹136 CPL, 10% conversion rate, and 6.4x average ROAS.",
    thumbnailImage: "/assets/success-stories/ismt-thumb.jpg",

    // ---- 1. Executive summary ----
    executiveSummary:
      "ISMT came in from a previous agency with a ₹480 CPL, irrelevant leads, no CRM, and no reliable conversion tracking. After fixing tracking end-to-end, integrating a CRM, and running continuous creative and landing page tests, CPL fell to ₹136 and conversion rate rose from 7% to 10%, with the account now averaging a 6.4x ROAS and 4.5x ROI on a monthly spend of roughly ₹80,000 to ₹1,00,000.",

    // ---- Four hero highlight cards (short one-liners, distinct from the full sections below) ----
    highlights: {
      challenge: "High CPL, no CRM, irrelevant leads from prior agency.",
      approach: "Fixed tracking, added CRM, tested creatives and pages.",
      timeline: "Oct 2025 to present, quarter over quarter gains.",
      result: "CPL down to ₹136, 10% conversion, 6.4x ROAS.",
    },

    // ---- SEO ----
    seo: {
      metaTitle: "ISMT Success Story: CPL Down 72%, 6.4x Average ROAS",
      metaDescription:
        "How fixing tracking, adding CRM, and continuous creative testing took ISMT's stock market training leads from a Rs 480 CPL and no CRM to a Rs 136 CPL, 10% conversion rate, and 6.4x average ROAS.",
    },

    // ---- 2. Client overview ----
    clientOverview: {
      about:
        "ISMT (Institute of Stock Market Training) provides online and offline training programs covering NSE, BSE, SEBI, NCFM, and NISM modules, spanning stock market basics, portfolio and wealth management, and technical and fundamental analysis.",
      industry: "B2C EdTech / Stock Market & Trading Education",
      scale: "Based in Pune; runs both online and offline cohorts; lead generation is the primary growth objective",
      context:
        "The account came from a previous agency struggling with data transparency, a high ₹480 CPL, low conversion rates, irrelevant leads, no CRM setup, and improper conversion and event tracking.",
    },

    // ---- 3. Challenge ----
    challenge: {
      summary:
        "The previous agency's account had a high CPL and low conversion rate, no CRM to actually track leads through to enrollment, and improper event and conversion tracking, so performance couldn't be trusted or optimized against.",
      points: [
        "Data transparency issues and unclear reporting from the previous agency",
        "High CPL (₹480) with a low conversion rate",
        "Irrelevant, low-quality leads coming through",
        "No CRM setup to track leads through to business conversion",
        "Improper conversion and event tracking, making optimization unreliable",
      ],
    },

    // ---- 4. Solution ----
    solution: {
      summary:
        "Fixed the tracking foundation first, then layered in CRM visibility and continuous testing, so every optimization decision after that point was based on real, trustworthy data.",
      strategies: [
        "Fixed conversion and event tracking using Google Tag Manager and Google Analytics",
        "Integrated a CRM for lead tracking, monitoring, and optimizing toward business conversion",
        "Ran ongoing A/B testing on creatives and landing pages to improve user engagement and tracking accuracy",
      ],
      platforms: ["Google Ads", "Meta Ads"],
    },

    // ---- 5. Implementation ----
    implementation: {
      timeline: "Oct 2025 to present, ongoing",
      milestones: [
        { phase: "Oct 2025", detail: "CPL down to ₹350 from a ₹480 baseline, at a 7% conversion rate" },
        { phase: "Jan 2026", detail: "CPL down to ₹320, conversion rate held steady at 7%" },
        { phase: "Apr 2026", detail: "CPL down to ₹174, conversion rate up to 10%" },
        { phase: "Jun 2026", detail: "CPL down to ₹136, conversion rate holding at 10%" },
      ],
      methodology:
        "Fixed tracking and CRM visibility first, then used continuous creative and landing page testing to compound gains quarter over quarter as the data became trustworthy.",
    },

    // ---- 6. Results ----
    results: {
      summary:
        "CPL fell steadily every quarter while conversion rate improved, taking the account from a ₹480 CPL under the previous agency to ₹136 by June 2026, with conversion rate rising from 7% to 10% over the same period. The account now averages a 6.4x ROAS and 4.5x ROI on a monthly spend of roughly ₹80,000 to ₹1,00,000, scaled month over month as the business needs it.",
      metrics: [
        { label: "CPL change (from ₹480 baseline)", value: "-72%", trend: "down-good" },
        { label: "Conversion rate", value: "10% (from 7%)", trend: "up-good" },
        { label: "Avg ROAS", value: "6.4x", trend: "up-good" },
        { label: "Avg ROI", value: "4.5x", trend: "up-good" },
      ],
    },

    // ---- 7. Evidence ----
    evidenceChart: {
      type: "line",
      labels: ["Oct '25", "Nov '25", "Dec '25", "Jan '26", "Feb '26", "Mar '26", "Apr '26", "May '26", "June '26", "July '26"],
      datasetLabel: "CPL (₹)",
      data: [350.00, 340.00, 359.08, 363.63, 286.38, 174.45, 271.64, 136.86, 132.00, 141.00],
      valuePrefix: "₹",
      valueSuffix: "",
    },
    evidenceNote:
      "CPL figures are drawn from ISMT's master sheet of monthly campaign data, Oct 2025 through Jul 2026. The trend was not a straight line: CPL held near ₹350 to ₹360 through Dec '25 and Jan '26, dropped sharply in Feb and Mar '26 to ₹174, rose again in Apr '26 to ₹272, then fell to its lowest point of ₹132 in June '26 before ticking back up slightly to ₹141 in July '26. Against the previous agency's ₹480 baseline, that is roughly a 71% reduction by the most recent month, and a 72.5% reduction at the lowest point (June '26).",

    // ---- 8. Lessons learned ----
    // Skipped for this case study at the client's request.
    lessonsLearned: [],

    // ---- 9. Testimonial ----
    testimonial: null,

    // ---- 10. ROI ----
    roi: {
      summary:
        "Monthly ad spend runs roughly ₹80,000 to ₹1,00,000, scaled month over month as the business needs it, with the team working closely with ISMT's sales team to maximize business conversion and revenue through enrollments. The account currently averages a 6.4x ROAS and a 4.5x ROI.",
      paybackPeriod: null,
    },

    // ---- 11. Next steps ----
    nextSteps: ["Expanding into the GCC market with a dedicated forex trading training program"],

    // ---- 12. Appendix (optional) ----
    appendix: {
      title: "Methodology & data notes",
      content:
        "Figures reflect performance data (CPL, conversion rate, ROAS, ROI) from ISMT's master sheet covering Oct 2025 through Jun 2026. As with the SoftwareSuggest and 5th Dimension Realty case studies, these numbers were not independently cross-referenced against dashboard or CRM screenshots.",
    },

    // ---- Bottom CTA ----
    cta: {
      headline: "Stuck with a high CPL and no CRM visibility?",
      body: "This is the same playbook I ran for ISMT: fix tracking first, add real CRM visibility, then compound gains with continuous testing.",
    },

    href: "/connect",
  },

  {
    slug: "one-empire-sleep-meta-ads-sales-growth",

    // ISO date this page went live, see note on the Madras Sarees entry above.
    publishedDate: "2026-09-23",

    // ---- Hero banner ----
    bannerImage: "/assets/success-stories/one-empire-sleep-thumb.jpg",

    // ---- Listing/card preview ----
    clientName: "One Empire Sleep",
    industry: "D2C Fashion / Lingerie & Sleepwear",
    tag: "D2C / Fashion",
    listingTitle: "3.5x average ROAS and $30K in profit for an Australian lingerie brand's first Meta Ads campaigns",
    listingSummary:
      "How building the entire marketing funnel from scratch, tracking, creative, and testing, took One Empire Sleep from zero campaign history to a 3.5x average ROAS and $30K in profit within six months.",
    thumbnailImage: "/assets/success-stories/one-empire-sleep-thumb.jpg",

    // ---- 1. Executive summary ----
    executiveSummary:
      "One Empire Sleep, an Australian lingerie and sleepwear brand, came in with no prior Meta Ads account or campaign history and needed a partner to build its entire sales funnel from scratch. Starting from a 2.5x ROAS in the first live month, the account reached a 3x ROAS with CAC down 20% by month two, then stabilized around a 3.5x average ROAS by month six alongside a 15% order spike after launching new SKUs, closing the period at $30K in profit.",

    // ---- Four hero highlight cards (short one-liners, distinct from the full sections below) ----
    highlights: {
      challenge: "Brand-new account, no history, funnel built from zero.",
      approach: "Built campaigns from scratch, then optimized CPA and AOV.",
      timeline: "Nov 2024 to Apr 2025, ROAS climbing steadily.",
      result: "3.5x avg ROAS and $30K profit by month six.",
    },

    // ---- SEO ----
    seo: {
      metaTitle: "One Empire Sleep Success Story: 3.5x ROAS, $30K Profit",
      metaDescription:
        "How building Meta Ads campaigns from scratch took Australian lingerie brand One Empire Sleep from zero ad history to a 3.5x average ROAS and $30K in profit within six months.",
    },

    // ---- 2. Client overview ----
    clientOverview: {
      about:
        "One Empire Sleep is an Australian lingerie and sleepwear brand founded in 2018, known for size-inclusive, ethically produced intimate apparel sold online and through retail partners.",
      industry: "D2C Fashion / Lingerie & Sleepwear",
      scale:
        "Founded 2018, headquartered in New South Wales, Australia; has sold over 25,000 lingerie sets and expanded into the Canadian market",
      context:
        "The brand had no prior paid social account or campaign history, and needed a Meta Ads partner to build the full marketing funnel and sales strategy from scratch.",
    },

    // ---- 3. Challenge ----
    challenge: {
      summary:
        "There was no existing Meta Ads account or past learnings to build from, so the entire campaign setup, sales strategy, and marketing funnel needed to be built from the ground up.",
      points: [
        "No previous learnings or existing ad account to build from",
        "Needed a complete Meta campaign setup built from scratch",
        "Needed a full marketing strategy and funnel built alongside the campaigns themselves",
      ],
    },

    // ---- 4. Solution ----
    solution: {
      summary:
        "Built every campaign from scratch, tracking, creative, and communication, then shifted focus from month two onward to reducing CPA and increasing AOV and ROAS.",
      strategies: [
        "Built all marketing campaigns from scratch, including new learnings and forecasts to work toward",
        "Set up full conversion tracking, creative, and communication for new campaigns",
        "Ran A/B testing across communication angles and offers",
        "From month two, shifted strategy toward reducing CPA, increasing AOV, and improving overall ROAS",
      ],
      platforms: ["Meta Ads"],
    },

    // ---- 5. Implementation ----
    implementation: {
      timeline: "Nov 2024 to Apr 2025",
      milestones: [
        { phase: "Nov 2024", detail: "New campaigns launched from scratch; 2.5x ROAS in the first live month" },
        { phase: "Dec 2024", detail: "3x ROAS achieved, with a 20% drop in CAC" },
        { phase: "Feb 2025", detail: "New SKUs introduced; 15% spike in orders and a rise in AOV" },
        { phase: "Apr 2025", detail: "3.5x average ROAS, a spike in retention, and $30K in profit" },
      ],
      methodology:
        "Built tracking, creative, and communication from zero in month one, then used A/B testing from month two onward to compound gains in CPA, AOV, and ROAS.",
    },

    // ---- 6. Results ----
    results: {
      summary:
        "Starting from zero campaign history, ROAS climbed from 2.5x in the first month to 3x by month two with CAC down 20%, then stabilized around a 3.5x average by month six, alongside a 15% spike in orders after launching new SKUs and $30K in profit by April 2025.",
      metrics: [
        { label: "Avg ROAS (from 2.5x)", value: "3.5x", trend: "up-good" },
        { label: "CAC change (by Dec 2024)", value: "-20%", trend: "down-good" },
        { label: "Order spike (after new SKUs)", value: "+15%", trend: "up-good" },
        { label: "Profit (by Apr 2025)", value: "$30K", trend: "up-good" },
      ],
    },

    // ---- 7. Evidence ----
    // No evidenceChart for this story: only three specific ROAS data points were
    // given across the six-month window (Nov, Dec, Apr), with the months in between
    // described only qualitatively. Plotting a line through that would imply a
    // smoother, more granular trend than what's actually verified.
    evidenceNote:
      "Figures are drawn from One Empire Sleep's internal master sheet covering Nov 2024 through Apr 2025 (ROAS, CAC, orders, AOV, and profit).",

    // ---- 8. Lessons learned ----
    lessonsLearned: [
      {
        challenge: "Lingerie product creative frequently triggered Meta's ad review process and got rejected.",
        resolution:
          "Reworked creative to stay within Meta's ad policies while still representing the product accurately, combined with ongoing A/B testing, which unlocked smooth campaign delivery and let sales scale gradually.",
      },
    ],

    // ---- 9. Testimonial ----
    testimonial: null,

    // ---- 10. ROI ----
    roi: {
      summary:
        "By April 2025, the account was generating $30K in profit at an average 3.5x ROAS, up from a 2.5x ROAS in its first live month, with CAC down 20% by month two and a 15% spike in orders after introducing new SKUs in February.",
      paybackPeriod: "Positive ROAS from the first live month (Nov 2024).",
    },

    // ---- 11. Next steps ----
    nextSteps: ["Launched new SKUs to expand the product range", "Set up an internal marketing team"],

    // ---- 12. Appendix (optional) ----
    appendix: {
      title: "Methodology & data notes",
      content:
        "Figures reflect performance data (ROAS, CAC, orders, AOV, and profit) shared directly by the One Empire Sleep team via their internal master sheet for Nov 2024 through Apr 2025. As with the SoftwareSuggest, 5th Dimension Realty, and ISMT case studies, these numbers were not independently cross-referenced against dashboard or Ads Manager screenshots. Separately, the Approach notes for this engagement mention ROAS reaching 4x by month six, while the dated timeline lists 3.5x for that same month (Apr 2025); this page uses the more specific, dated figure (3.5x).",
    },

    // ---- Bottom CTA ----
    cta: {
      headline: "Launching a new D2C brand with zero ad account history?",
      body: "This is the same playbook I ran for One Empire Sleep: build tracking and creative right from day one, then compound gains in CPA, AOV, and ROAS month over month.",
    },

    href: "/connect",
  },

  // Add more story objects here, one per project, following the same shape.
];

// The six real services, matching what's actually offered.
export const services = [
  { title: "Performance & growth marketing", desc: "High-intent acquisition across Google, Meta, LinkedIn & more, built for conversions, ROAS, and revenue." },
  { title: "Growth strategy & funnel design", desc: "Full-funnel strategy aligning audience, messaging, media, and offers to maximize conversion efficiency." },
  { title: "Lead generation & revenue scaling", desc: "Predictable lead systems for B2B, D2C, SaaS, and education, optimized for quality, velocity, and cost." },
  { title: "Conversion rate optimization", desc: "Systematic optimization of landing pages, creatives, and forms using behavioral data and testing." },
  { title: "Analytics, tracking & attribution", desc: "Clean GA4, GTM, and dashboards so every decision is backed by accurate, actionable data." },
  { title: "Consulting & agency enablement", desc: "Strategic consulting and white-label support for founders, in-house teams, and agencies." },
];

// Real client testimonials. Photos use initials avatars rather than hotlinked/guessed image
// URLs. Swap in real hosted photos once uploaded to the site's own media library.
export const aboutContent = {
  intro: {
    heading: "What I Actually Do?",
    body: "I build acquisition systems that deliver predictable growth, combining strategic rigor with hands-on execution across paid channels.",
  },
  drives: {
    heading: "What Drives My Work?",
    lead: "Most marketers optimize campaigns. I architect growth systems.",
    body: "My approach starts upstream: ideal customer clarity, funnel diagnostics, creative-data alignment, and only then channel execution. This means campaigns that scale profitably from month one, not month six.",
  },
  whatYouGet: [
    { title: "Strategic Foundation", desc: "Customer research, positioning, funnel mapping" },
    { title: "Channel Execution", desc: "Google, Meta, LinkedIn campaign build & scale" },
    { title: "Performance Operations", desc: "Tracking architecture (GA4, GTM, attribution)" },
    { title: "Continuous Optimization", desc: "Weekly testing cadence, creative iteration, audience expansion" },
  ],
  industries: ["SaaS & B2B Tech", "D2C & Fashion", "Healthcare", "Edtech", "Foodtech", "Retail"],
  closing: "Together let's keep going.",
  logosTagline: "Scaling businesses globally across industries through performance-driven growth strategies.",
};

// Blog categories for the filter pills on /category/blogs.
export const blogCategories = ["All", "Performance Marketing", "Growth Strategy", "SEO & GEO", "Case Studies", "Tools & AI"];

// Blog posts. `body` holds the full write-up as an array of typed content blocks:
//   paragraph: { type: "p", text }
//   heading:   { type: "h2"/"h3"/"h4", text } (post title is the only <h1>)
//   list:      { type: "ul", items: [...] }
//   table:     { type: "table", headers: [...], rows: [[...]] }
//   image:     { type: "image", src, alt, caption } (inline image with alt text + caption)
// Leave `body` undefined/null until the real article is ready; the detail page shows a
// "full article coming soon" state rather than fabricated content.
export const blogPosts = [
  {
    slug: "google-ads-2026-updates-vertical-aware-automation",
    category: "Performance Marketing",
    title: "How Google Ads' 2026 Updates Are Built for Your Industry, Not Just Your Budget",
    excerpt: "Google Ads' 2026 updates bring vertical-aware automation. See what changed for education, finance, and e-commerce accounts, complete with tables and a decision flowchart.",
    metaDescription: "Google Ads' 2026 updates bring vertical-aware automation. See what changed for education, finance, and e-commerce accounts, complete with tables and a decision flowchart.",
    date: null,
    image: { src: "/assets/google-ads-2026-feature.png", alt: "How Google Ads' 2026 updates are built for your industry, not just your budget" },
    body: [
      { type: "p", text: "Google Ads no longer treats every advertiser the same way. The platform's 2026 updates lean hard into industry-aware automation: bidding, creative, and audience systems that behave differently depending on whether you sell trading courses, admissions seats, forex accounts, or fashion accessories." },
      { type: "p", text: "For agencies and in-house teams managing multiple verticals, this is the difference between fighting the algorithm and steering it." },
      { type: "p", text: "This post breaks down what changed, why it's vertical-specific, and how to actually use it to scale accounts, complete with tables, a decision flowchart, and links to dig deeper." },

      { type: "h2", text: "Why \"Vertical-Aware\" Google Ads Is a Real Shift, Not Marketing Speak" },
      { type: "h3", text: "The core problem it solves" },
      { type: "p", text: "Performance benchmarks vary wildly by industry: a 3% conversion rate is excellent for B2B lead gen and mediocre for e-commerce. Improvements that actually move accounts forward tend to be structural: landing page alignment, tighter match types, and audience signals layered on top of first-party data. Google's newer systems are designed to apply that logic automatically, per vertical, instead of forcing one blended playbook." },

      { type: "h3", text: "What's driving it" },
      { type: "ul", items: [
        "Smart Bidding models now weigh auction density and buyer intent patterns specific to a category",
        "Multi-industry advertisers can now run experiments comparing strategies like Maximize Conversions vs. Target CPA, because what converts in one vertical often underperforms in another",
        "Google has also improved budget allocation logic, shifting spend more aggressively between placements based on real-time performance, which compounds differently depending on category volatility (finance vs. education, for example)",
      ]},

      { type: "h2", text: "The 2026 Update Stack: What Actually Changed" },
      { type: "h3", text: "Summary table" },
      { type: "table",
        headers: ["Update", "What it does", "Vertical impact"],
        rows: [
          ["Real-time budget reallocation", "Shifts spend across placements dynamically based on live performance signals", "High-ticket/finance sees faster reallocation; education sees smoother seasonal shifts"],
          ["Target CPA / Target ROAS renamed", "\"Maximize conversions with a Target CPA\" is now just Target CPA; \"Maximize conversion value with a Target ROAS\" is now Target ROAS (no behavior change)", "Naming clarity for reporting across mixed-vertical dashboards"],
          ["AI Max for Search", "Targets conversational, multi-step queries rather than pure keyword matches", "Strong for research-heavy verticals: finance, admissions, B2B"],
          ["Demand Gen expansion", "Demand Gen now runs across the entire Display Network, including industry blogs, trade publications, and research portals, not just YouTube, Discover, and Gmail", "High-value for B2B and education, where buyers research before converting"],
          ["AI-generated creative labeling", "Mandatory disclosure tags on AI-made assets", "In trust-sensitive industries like finance or healthcare, the label may affect CTR; in others it may not. Test before assuming."],
          ["Performance Max transparency updates", "Google addressed advertiser concerns about limited budget visibility inside PMax", "Matters most for accounts running PMax across multiple product/program lines"],
        ]
      },

      { type: "h3", text: "Quick visual: where automation is shifting" },
      { type: "table",
        headers: ["Area", "Automation level"],
        rows: [
          ["Automated spend control", "~80%"],
          ["Manual/segment overrides", "~25%"],
          ["Creative still human-led", "~40%"],
        ]
      },
      { type: "p", text: "Directional: actual mix depends on account maturity and vertical trust sensitivity." },

      { type: "h2", text: "Industry-by-Industry Breakdown" },
      { type: "h3", text: "Education / Admissions (e.g., engineering & management institutes)" },
      { type: "ul", items: [
        "Long consideration cycles → AI Max for Search captures multi-step research queries (\"best BTech colleges Pune placements\") instead of relying only on exact keywords",
        "Demand Gen on Display Network now reaches students on research portals and forums during the decision phase, not just YouTube",
        "Seasonal admission cycles benefit from real-time budget reallocation, with spend shifting toward high-intent periods (application deadlines) automatically",
      ]},
      { type: "h3", text: "Finance / Forex / Trading Education" },
      { type: "ul", items: [
        "Trust-sensitive category → AI-generated creative labels can move CTR; test labeled vs. human-made assets before scaling either",
        "Target CPA/ROAS renaming doesn't change strategy, but keep reporting templates updated so client-facing dashboards stay consistent",
        "High CPC volatility makes real-time budget shifts more impactful here than in stable verticals. Monitor daily, not weekly.",
      ]},
      { type: "h3", text: "E-commerce (accessories, retail, D2C)" },
      { type: "ul", items: [
        "Demand Gen's storefront-style formats and catalog integration help visual categories convert directly from discovery",
        "PMax transparency updates give better visibility into channel-level ROAS, which is critical for margin-sensitive retail accounts",
        "Broad match + RSAs still need tight audit; broad match in 2026 can pull in irrelevant clicks that quietly suppress conversion rate without capping spend",
      ]},
      { type: "h3", text: "B2B / Multi-Industry Service Providers" },
      { type: "ul", items: [
        "The experiments feature is especially useful when serving multiple industries, since a winning strategy in one vertical can underperform in another",
        "Demand Gen's reach into trade publications and industry blogs supports longer B2B research journeys",
        "Recommend running per-vertical experiments rather than one account-wide bidding strategy",
      ]},

      { type: "h2", text: "Decision Flow: Applying Vertical Logic to Your Account" },
      { type: "image", src: "/assets/google-ads-decision-flowchart.png", alt: "Decision flowchart for applying vertical-specific Google Ads strategy based on trust sensitivity, research cycle length, and CPC volatility", caption: "Use this flow to decide whether to prioritize creative testing, AI Max + Demand Gen, real-time budget monitoring, or standard bidding experiments for your account." },

      { type: "h2", text: "Insights for Agencies Managing Multiple Verticals" },
      { type: "ul", items: [
        "Don't apply one bidding playbook across accounts. What converts in one industry can genuinely underperform in another, even with identical budgets.",
        "Audit conversion tracking before trusting new budget logic, since misattributed conversions get amplified faster under real-time reallocation",
        "Weekly check-ins are no longer enough for volatile verticals. Teams that review performance only biweekly risk misreading shifts and reallocating budget based on misleading data.",
        "Test AI creative labeling per vertical, not account-wide, since the same label can be neutral in retail and costly in finance",
        "Treat Google's own performance-lift stats as directional: reach-expansion figures like +18–27% are platform-wide averages from Google's internal data, not independently verified, and vary by account and vertical",
      ]},

      { type: "h2", text: "FAQs" },
      { type: "h4", text: "Does AI Max replace manual keyword targeting?" },
      { type: "p", text: "No. It layers conversational query matching on top of existing Search campaigns. Manual control still matters for brand terms and compliance-sensitive keywords." },
      { type: "h4", text: "Is Performance Max now vertical-aware?" },
      { type: "p", text: "Not natively. It optimizes to your feed and signals, but the 2026 transparency updates make it easier to see how budget splits across your product/program lines, which is what enables vertical-level decisions." },
      { type: "h4", text: "Should every account switch bidding strategies because of the renaming?" },
      { type: "p", text: "No. Target CPA and Target ROAS are just renamed, not behaviorally different. No action needed beyond updating internal reporting labels." },

      { type: "h2", text: "References" },
      { type: "ul", items: [
        "Google Ads Updates In 2026: Every Major Change (groas.ai)",
        "Google Ads June 2026 Bidding & Budgeting Overhaul (Digital Applied)",
        "New Features & Announcements (Google Ads Help)",
        "Google Ads 2025 Year-in-Review (ALM Corp)",
        "What 2025 Google Ads Updates Mean for B2B Brands (Kliq Interactive)",
        "Advanced Google Ads Techniques for 2026 (PPC Hero)",
        "Google Ads Conversion Rates by Industry: 2026 Guide (Kreativa Group)",
        "Latest Google Ads Product Announcements (Google Business)",
      ]},
    ],
    ctaText: "If your Google Ads account is still running one blended strategy across education, finance, or retail clients, you're leaving performance on the table. Book a free account audit to see where vertical-specific bidding, creative testing, and budget logic can lift your ROAS this quarter.",
    ctaLabel: "Get Your Free Audit",
    ctaHref: "/connect",
  },
  {
    slug: "marketing-funnel-is-dead-growth-teams-track-instead",
    category: "Growth Strategy",
    title: "The Marketing Funnel Is Dead: Here's What Growth Teams Track Instead",
    excerpt: "The linear marketing funnel no longer matches buyer behavior. See what growth teams track instead: flywheels, growth loops, and cohort retention.",
    metaDescription: "The linear marketing funnel no longer matches buyer behavior. See what growth teams track instead: flywheels, growth loops, and cohort retention.",
    date: null,
    image: { src: "/assets/marketing-funnel-dead-feature.png", alt: "The marketing funnel is dead: here's what growth teams track instead, illustrated with a cracked funnel and a growth loops dashboard" },
    body: [
      { type: "p", text: "For 15+ years, marketers have drawn the same shape on every strategy slide: Awareness → Consideration → Conversion. Clean, linear, easy to present." },
      { type: "p", text: "The problem? Buyers stopped moving that way a long time ago." },
      { type: "p", text: "This post breaks down why the funnel model fails modern buyer behavior, what growth teams track instead, and how to actually restructure your thinking, without needing a single ad dollar to prove the point." },

      { type: "h2", text: "Why the Funnel Stopped Working" },
      { type: "h3", text: "The behavior it assumes" },
      { type: "p", text: "The funnel assumes a customer enters at \"awareness,\" moves in one direction, and exits at \"purchase.\" Neat, sequential, one-way." },
      { type: "h3", text: "The behavior that actually happens" },
      { type: "p", text: "Real buyers bounce between research and evaluation constantly: comparing tabs, watching a video, checking reviews, forgetting about it for a week, then coming back. Google's own \"Messy Middle\" research describes this as a continuous loop between exploration and evaluation, not a straight line." },
      { type: "h3", text: "What broke the model" },
      { type: "ul", items: [
        "Privacy changes weakened tracking and remarketing accuracy",
        "Multi-device journeys: most buyers touch a brand 7–13 times across different channels before deciding",
        "Faster decision-making: comparison tools and reviews compress research time from weeks to minutes",
        "Post-purchase behavior matters now: funnels stop at conversion, but referrals, repeat purchases, and reviews all happen after",
      ]},

      { type: "h2", text: "What's Replacing the Funnel" },
      { type: "h3", text: "The Flywheel Model" },
      { type: "p", text: "Popularized by HubSpot, instead of ending at conversion, the customer sits at the center of a continuous cycle: Attract → Engage → Delight. Reducing friction anywhere in the cycle speeds up the entire system. Faster onboarding doesn't just help retention. It increases referral velocity, which feeds new acquisition." },
      { type: "h3", text: "Growth Loops" },
      { type: "p", text: "Common in product-led and SaaS companies: a loop where output from one user becomes input for acquiring the next (content loops, referral loops, viral loops). Unlike a funnel, a loop compounds instead of resetting to zero every cycle." },
      { type: "h3", text: "Influence Maps" },
      { type: "p", text: "An alternative proposed by BCG for high-consideration purchases (finance, big-ticket retail), mapping the actual sources a buyer touches (ads, search, social, review sites, word of mouth) rather than forcing behavior into stages." },
      { type: "h3", text: "Side-by-side comparison" },
      { type: "table",
        headers: ["", "Funnel", "Flywheel / Loops"],
        rows: [
          ["Shape", "Linear, one-way", "Circular, compounding"],
          ["Ends at", "Conversion", "Never; retention feeds acquisition"],
          ["Customer role", "Passive (moved through stages)", "Active (drives growth via referrals/content)"],
          ["Best suited for", "Simple, one-time purchases", "Subscription, repeat-purchase, community-driven brands"],
          ["Risk", "Ignores post-purchase value", "Needs strong product/CX to actually spin"],
        ]
      },

      { type: "h2", text: "What Growth Teams Actually Track Now" },
      { type: "h3", text: "Metrics that replace \"funnel conversion rate\"" },
      { type: "table",
        headers: ["Old funnel metric", "What growth teams track instead", "Why it's better"],
        rows: [
          ["Click-through rate", "Activation rate (% reaching \"aha moment\")", "Predicts retention, not just interest"],
          ["Conversion rate", "Cohort retention curve", "Shows if value compounds or decays over time"],
          ["Cost per lead", "CAC payback period", "Ties spend to actual cash recovery speed"],
          ["Total leads", "Referral / K-factor", "Measures organic, self-sustaining growth"],
          ["Funnel drop-off", "Engagement velocity", "Tracks how fast users move deeper into product/content, not just \"did they convert\""],
        ]
      },

      { type: "h3", text: "Quick visual: retention curve shape tells the real story" },
      { type: "table",
        headers: ["Week", "Good retention (flattens)", "Bad retention (decays to zero)"],
        rows: [
          ["1", "100%", "100%"],
          ["2", "80%", "80%"],
          ["3", "60%", "60%"],
          ["4", "50%", "40%"],
          ["8", "45% (flattens)", "0% (decays away)"],
        ]
      },
      { type: "p", text: "A curve that flattens signals product-market fit. A curve that decays to zero means no amount of top-of-funnel spend will fix the leak." },

      { type: "h2", text: "Flowchart: Diagnosing Your Growth Model" },
      { type: "h4", text: "Step 1: Are customers coming back without new campaigns?" },
      { type: "ul", items: [
        "Yes → You likely have a flywheel/loop already. Measure retention and referral velocity to see how strong it is.",
        "No → You're funnel-dependent: growth stops the moment spend stops. Move to Step 2.",
      ]},
      { type: "h4", text: "Step 2: Are new users reaching the \"aha moment\"? (Activation rate)" },
      { type: "ul", items: [
        "Yes → Fix onboarding friction to speed up the referral loop.",
        "No → Fix product value before spending more on acquisition.",
      ]},

      { type: "h2", text: "Insights for Building a Non-Linear Growth System" },
      { type: "ul", items: [
        "Retention is the new top of funnel: a compounding loop only spins if people stay, so fixing churn beats adding more top-of-funnel volume",
        "Track cohorts, not aggregates: a flat \"conversion rate\" hides whether last month's customers are more or less loyal than this month's",
        "Map real touchpoints, don't force stages. For complex or high-ticket purchases, an influence map of actual channels beats an artificial linear path.",
        "Friction reduction compounds: improving one weak point (onboarding, support response time) often lifts multiple metrics at once because the system is circular, not sequential",
        "AI is accelerating non-linearity, not fixing it. Buyers now get answers at any stage instantly, which makes rigid, staged journeys even less realistic than before.",
      ]},

      { type: "h2", text: "FAQs" },
      { type: "h4", text: "Should I completely stop using the funnel as a concept?" },
      { type: "p", text: "No. It's still a useful mental model for understanding that buyers have different needs at different points. What's outdated is treating it as a fixed, linear sequence to measure and organize your team around." },
      { type: "h4", text: "Is the flywheel model only for subscription businesses?" },
      { type: "p", text: "No, though it's most visible there. Any business with repeat purchases, referrals, or word-of-mouth potential benefits from measuring momentum instead of one-time conversion." },
      { type: "h4", text: "What's the first metric I should start tracking if I'm funnel-only today?" },
      { type: "p", text: "Cohort retention. It's the single clearest signal of whether your growth is compounding or just resetting every month." },

      { type: "h2", text: "References" },
      { type: "ul", items: [
        "The Marketing Funnel Is Dead: Here's What Replaced It (Sphere Agency)",
        "The Funnel Is Dead. Here's What's Replacing It (Future-Proof Marketing)",
        "From Funnel to Flywheel: The New Brand Growth Model (The Brief)",
        "2026 Is the Year the Marketing Funnel Finally Dies (Retail Banker International)",
        "Funnel vs Flywheel: Which Model Actually Drives Growth (310 Creative)",
        "Marketing Funnels vs Flywheels: Which Model Works Better in 2026 (Web Marketing Academy)",
        "The AI Marketing Funnel: From Linear Paths to Flywheels (Tank)",
      ]},
    ],
    ctaText: "If your dashboards still center on funnel conversion rate, you're measuring last decade's model. Start with one shift: pull your cohort retention curve for the last 6 months and see what shape it actually makes.",
    ctaLabel: "Let's talk about building your growth metrics stack",
    ctaHref: "/connect",
  },
  {
    slug: "ai-ad-platforms-reshaping-roas-cpl-cac-2026",
    category: "Tools & AI",
    title: "AI Ad Platforms Are Reshaping ROAS, CPL & CAC in 2026",
    excerpt: "If your CPL jumped or your ROAS swung with no changes on your end, it's not a fluke. Google AI Max and Meta Advantage+ are rewriting how campaigns run in real time.",
    metaDescription: "Google AI Max and Meta Advantage+ are turning ROAS, CPL, and CAC into moving targets. Here is why it's happening, what the data shows, and how marketers should respond.",
    date: null,
    image: { src: "/assets/blog-ai-ad-platforms-roas.png", alt: "AI ad platforms are reshaping ROAS, CPL, and CAC in 2026: Google Ads, Meta, and LinkedIn feeding into an AI layer with a performance dashboard" },
    body: [
      { type: "p", text: "If your CPL jumped 20% last month with no changes on your end, you are not imagining it. If your ROAS on a \"stable\" campaign suddenly swung in either direction, that is not a fluke either." },
      { type: "p", text: "Google and Meta have quietly rebuilt the machinery behind every campaign you run. Both platforms are now leaning on real-time machine learning to decide who sees your ad, what it says, and how your budget gets spent, often minute by minute." },
      { type: "p", text: "The result: the old idea of a \"stable\" account is fading. Metrics that used to hold steady for months now move constantly while the models learn, test, and recalibrate." },
      { type: "p", text: "This post breaks down what is actually changing, why your numbers are fluctuating, and what marketers need to do differently." },

      { type: "h2", text: "The Shift Nobody Formally Announced" },
      { type: "p", text: "For years, performance marketing ran on a simple logic:" },
      { type: "ul", items: [
        "You set the rules (keywords, audiences, bids, placements)",
        "The platform followed them",
        "You adjusted manually when something underperformed",
      ]},
      { type: "p", text: "That model is disappearing." },
      { type: "ul", items: [
        "Google and Meta are shifting from rule-based execution to dynamic, self-optimizing automation",
        "Instead of following instructions, the platforms are now predicting outcomes and adjusting targeting, creative, and budget in real time",
        "Marketers are handing over more of the decision layer, in exchange for scale and reach they cannot replicate manually",
      ]},
      { type: "p", text: "This is not a future trend. It is already the default setting on new campaigns in both Google Ads and Meta Ads Manager." },

      { type: "h2", text: "What's Actually Changing on Google and Meta" },
      { type: "h3", text: "Google AI Max for Search" },
      { type: "p", text: "Google's AI Max is not a new campaign type. It is an automation layer sitting inside standard Search campaigns, combining three features:" },
      { type: "ul", items: [
        "Search term matching: expands targeting beyond your keyword list using broad match and \"keywordless\" intent matching",
        "Text customization: generates and adapts headlines and descriptions in real time based on the query and your landing page",
        "Final URL expansion: automatically redirects users to whichever page on your site Google predicts will convert best",
      ]},
      { type: "p", text: "Google has confirmed it will auto-migrate Campaign-level Broad Match and Automatically Created Assets to AI Max starting September 2026, with Dynamic Search Ads following by February 2027. AI Max is quickly becoming the default, not an opt-in." },
      { type: "p", text: "What the independent data shows:" },
      { type: "table",
        headers: ["Source", "Reported outcome"],
        rows: [
          ["Google's own benchmark", "Around 14% more conversions or conversion value at similar CPA, rising to 27% for keyword-heavy accounts"],
          ["Smarter Ecommerce (250+ campaign study)", "Median revenue lift of 13%, but median CPA rose 16%; only 22% of campaigns hit their original ROAS target"],
          ["Independent industry testing cited by ALM Corp", "84% of advertisers reported neutral or negative results despite the headline promise"],
        ]
      },
      { type: "p", text: "The takeaway: the same feature produces very different outcomes depending on account structure, feed quality, and how closely it is monitored." },

      { type: "h3", text: "Meta Advantage+ and the Andromeda Engine" },
      { type: "p", text: "Meta has gone further, consolidating manual and automated campaign flows into one AI-first system:" },
      { type: "ul", items: [
        "Advantage+ now handles targeting, budget allocation, bidding, and creative variation by default on new campaigns",
        "The underlying model, Andromeda, reads creative signals (visuals, tone, pacing) to find buyers, not just declared audience settings",
        "Meta has stated a goal of fully automated, end-to-end ad creation by the end of 2026, where an advertiser supplies a product URL and budget, and AI handles the rest",
      ]},
      { type: "p", text: "What the data shows:" },
      { type: "table",
        headers: ["Feature / Metric", "Reported impact"],
        rows: [
          ["Advantage+ vs manual campaigns (Meta reported)", "Average 22% higher ROAS"],
          ["Predictive Budget Allocation (2026 update)", "8 to 15% ROAS improvement in early tests"],
          ["Campaign consolidation into Advantage+", "Up to 32% CPA reduction for accounts that migrated from fragmented structures"],
          ["Independent incrementality testing (Haus, 640 tests)", "Advantage+ campaigns underperformed manual campaigns over time despite strong platform-reported ROAS"],
        ]
      },
      { type: "p", text: "Notice the pattern again: platform-reported numbers and independently verified incrementality do not always agree." },

      { type: "h2", text: "Why Your ROAS, CPL, and CAC Are Fluctuating Right Now" },
      { type: "h3", text: "The Model Is Still Learning You" },
      { type: "p", text: "Both platforms need a live feedback loop to optimize well. Every time you:" },
      { type: "ul", items: [
        "Launch a new campaign",
        "Edit a live ad group",
        "Change a budget significantly",
        "Swap creative assets",
      ]},
      { type: "p", text: "...the model treats it as new information and re-enters a learning phase. During this phase, performance is genuinely unstable, not broken." },

      { type: "h3", text: "A Simple Way to Visualize It" },
      { type: "ul", items: [
        "New signal: a launch, an edit, a budget change, or new creative",
        "The platform's AI re-reads the signal",
        "Targeting, bidding, and creative shift in real time",
        "Short-term volatility shows up in ROAS, CPL, and CAC",
        "The model stabilizes, if it's fed clean, consistent data",
        "Metrics normalize, until the next signal or platform update",
      ]},
      { type: "p", text: "The loop does not fully stop. Every algorithm update, seasonal shift, or account change restarts part of it. This is why \"wait for it to normalize\" is only half the answer. It normalizes only if the inputs stay clean and consistent." },

      { type: "h3", text: "It Is Happening Across Every Industry" },
      { type: "p", text: "This is not unique to e-commerce or higher education. The same volatility shows up across verticals, just with different intensity:" },
      { type: "table",
        headers: ["Industry", "What's typically affected"],
        rows: [
          ["Higher education / admissions", "CPL swings as AI Max expands query matching beyond exact program keywords"],
          ["E-commerce / D2C", "ROAS volatility tied to creative fatigue and Advantage+ budget reallocation"],
          ["Finance / trading platforms (regulated)", "Compliance-sensitive copy generated by AI needs manual review, adding a control layer"],
          ["B2B / SaaS", "Longer sales cycles mean the AI has fewer, slower signals to learn from, so CAC stays noisy longer"],
        ]
      },

      { type: "h2", text: "The Mistake Marketers Keep Making" },
      { type: "p", text: "The most common failure pattern right now:" },
      { type: "ul", items: [
        "Launch the campaign with AI Max or Advantage+ enabled",
        "Set the budget and target CPA/ROAS",
        "Walk away and check back in two or three weeks",
        "Get surprised by a CPL spike or ROAS drop that had early warning signs a week earlier",
      ]},
      { type: "p", text: "Why this fails now, when it used to be fine:" },
      { type: "ul", items: [
        "Rule-based campaigns changed slowly. Weekly check-ins were enough.",
        "Self-optimizing campaigns can shift meaningfully within days, sometimes hours, especially right after a launch or edit.",
        "Every manual edit resets part of the learning window (Meta needs roughly 7 days to recalibrate after a significant creative or budget change).",
      ]},
      { type: "p", text: "Negligence used to cost you a slow leak. Now it can cost you a full learning cycle, and a learning cycle is not cheap when budgets are live." },

      { type: "h2", text: "What This Means for You: From Control to Stewardship" },
      { type: "p", text: "Marketers are not losing control entirely. Control is shifting from granular manual levers to the quality of what you feed the system. Three areas matter most." },

      { type: "h3", text: "Feed Optimization" },
      { type: "h4", text: "Why it matters" },
      { type: "p", text: "Google's Final URL expansion and Meta's Advantage+ Creative both depend on your feed and landing pages to make good decisions. A weak feed produces weak automation, no matter how advanced the model is." },
      { type: "h4", text: "What to do" },
      { type: "ul", items: [
        "Keep product/landing page data structured, accurate, and current",
        "Use schema markup so AI systems can parse content correctly",
        "Rebuild key landing pages around the specific questions your audience is actually asking, not just keyword density",
      ]},

      { type: "h3", text: "First-Party Data Integration" },
      { type: "h4", text: "Why it matters" },
      { type: "p", text: "Both platforms optimize against the conversion signals you give them. Noisy or incomplete signals mean the model calibrates against bad data, and no amount of budget fixes that." },
      { type: "h4", text: "What to do" },
      { type: "ul", items: [
        "Implement server-side tracking (Conversions API on Meta, enhanced conversions on Google)",
        "Make sure your primary conversion event reflects a genuine business outcome (purchase, qualified lead), not a proxy metric like page views",
        "Check Event Match Quality regularly, not just at setup",
      ]},

      { type: "h3", text: "Creative Diversification" },
      { type: "h4", text: "Why it matters" },
      { type: "p", text: "On Meta specifically, creative has become a targeting signal, not just a message. Andromeda reads visual and tonal cues to decide who sees an ad. Fewer, older creatives mean a narrower, staler signal." },
      { type: "h4", text: "What to do" },
      { type: "ul", items: [
        "Refresh creative every 2 to 4 weeks at minimum",
        "Test genuinely different concepts and formats, not just minor copy tweaks",
        "Treat AI-generated creative as a first draft to refine, not a finished asset",
      ]},

      { type: "h2", text: "A Practical Weekly Monitoring Checklist" },
      { type: "table",
        headers: ["Day / Frequency", "What to check"],
        rows: [
          ["Daily (first 7 days after any launch/edit)", "Spend pacing, early CPL/CPA signals, disapprovals"],
          ["Weekly", "ROAS/CPL/CAC trend vs. prior week, search term/placement reports, creative fatigue (frequency, CTR decline)"],
          ["Bi-weekly", "Refresh underperforming creative, review AI-generated copy for accuracy and compliance"],
          ["Monthly", "Feed and landing page audit, conversion event quality check, incrementality sanity check against platform-reported numbers"],
        ]
      },

      { type: "h2", text: "Key Takeaways" },
      { type: "ul", items: [
        "Google and Meta have both moved from static, rule-based execution to real-time, self-optimizing automation",
        "This is causing genuine, temporary instability in ROAS, CPL, and CAC as models learn and recalibrate, and it affects every industry, not just one",
        "Platform-reported performance and independently measured incrementality frequently disagree, so treat headline benchmarks with healthy skepticism",
        "The \"launch and leave\" approach is the single biggest risk right now. Negligence during a live learning phase compounds quickly",
        "Control has shifted from manual levers to input quality: feed optimization, first-party data, and creative diversification are now the real performance levers",
      ]},
    ],
    ctaText: "If your account's numbers have been harder to predict lately, that is not a sign you are doing something wrong. It is a sign the platforms changed underneath you. I help higher-ed, D2C, and finance brands audit their Google Ads and Meta Ads accounts for exactly this kind of AI-driven volatility, and rebuild the monitoring habits that keep performance steady while the algorithms keep learning.",
    ctaLabel: "Get in touch for a campaign audit",
    ctaHref: "/connect",
  },
];

export const testimonials = [
  {
    photo: "/assets/michael-howard.jpeg",
    initials: "MH",
    stat: "4.8x",
    statLabel: "ROAS achieved",
    quote: "We were stuck at 2.2 ROAS and burning through budget with no clear path forward. Within 30 days, we were at 4.8 ROAS and scaling profitably.",
    name: "Michael Howard",
    role: "Product Owner, Skedda",
  },
  {
    photo: "/assets/milly-smith.webp",
    initials: "MS",
    stat: "15,000+",
    statLabel: "qualified leads in 6 months",
    quote: "Abhinav focused on pipeline quality and revenue contribution, not just volume. He helped us generate over 15,000 qualified leads in 6 months.",
    name: "Milly Smith",
    role: "Sales Head, GSDC",
  },
  {
    photo: "/assets/rohan-kannirwar.png",
    initials: "RK",
    stat: "90 days",
    statLabel: "to profitability",
    quote: "We went from burning cash to achieving profitable unit economics within 90 days. Abhinav's data-driven guidance and accountability set him apart. I always recommend him to founders and businesses looking to scale.",
    name: "Rohan Kannirwar",
    role: "Founder & CEO, Spike ROAS",
  },
];

// "Latest insights": real posts only. Replace/extend as new content is published.
// This should eventually be wired to a live WordPress query (top 5 most recent posts
// across blog + case study categories) rather than a static list.
export const insights = [
  {
    tag: "Case study",
    title: "₹4+ crore in revenue for Madras Sarees, a D2C saree brand",
    href: "/success-stories/madras-sarees-revenue-growth",
  },
  {
    tag: "Blog",
    title: "How Google Ads' 2026 Updates Are Built for Your Industry",
    href: "/blog/google-ads-2026-updates-vertical-aware-automation",
  },
  {
    tag: "Blog",
    title: "The Marketing Funnel Is Dead: Here's What Growth Teams Track Instead",
    href: "/blog/marketing-funnel-is-dead-growth-teams-track-instead",
  },
];

// Real, relevant FAQs for a marketing consulting business, not generic SaaS support tabs.
export const faqs = [
  {
    q: "How do you measure success?",
    a: "Every engagement starts with agreed KPIs, usually ROAS, CPL, or revenue growth, reported against actual closed sales, not just ad-platform leads.",
  },
  {
    q: "What size businesses do you work with?",
    a: "Startups, D2C brands, agencies, and established businesses across education, finance, fashion, and more. The approach adapts to your scale and budget.",
  },
  {
    q: "Do you work with agencies too?",
    a: "Yes. Consulting and white-label support for agencies that need extra strategic or execution capacity without adding full-time headcount.",
  },
  {
    q: "Which platforms do you run campaigns on?",
    a: "Google Search, Performance Max, Shopping, Meta, LinkedIn, Microsoft/Bing, Amazon, Pinterest, Snapchat, and TikTok. The specific mix depends on where your buyers actually are.",
  },
  {
    q: "How do we get started?",
    a: "Book a free strategy call, or use the discovery tool to generate a first-pass plan instantly before we even talk.",
  },
];
